import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  App,
  Button,
  Card,
  Col,
  Flex,
  Form,
  InputNumber,
  Row,
  Skeleton,
  Switch,
  Typography,
} from "antd";
import {
  PlatformSettings,
  useGetPlatformSettingsQuery,
  useUpdatePlatformSettingsMutation,
} from "api";
import { useAppSelector } from "store/hooks";
import { getRouteByName } from "libs";
import { getErrorMessage } from "components";
import { AppUpdateSettingsCard } from "./AppUpdateSettingsCard";

type PlatformSettingsFormValues = Pick<
  PlatformSettings,
  | "postExpireDays"
  | "paidPostPricePerDay"
  | "verifiedBadgePricePerMonth"
  | "dailyQuotasEnabled"
  | "maxPostsPerDay"
  | "maxVideoPostsPerDay"
  | "maxCommentsPerDay"
  | "maxVideoCommentsPerDay"
  | "maxVideoDurationSeconds"
  | "maxEventLiveDurationSeconds"
  | "eventMaxRadiusKm"
>;

const MAX_VIDEO_DURATION_CEILING_SECONDS = 600;
const MAX_EVENT_LIVE_DURATION_CEILING_SECONDS = 14_400;
const MIN_EVENT_LIVE_DURATION_SECONDS = 30;
const EVENT_MAX_RADIUS_CEILING_KM = 20_000;
const EVENT_MIN_RADIUS_KM = 1;

export const PlatformSettingsPage = () => {
  const navigate = useNavigate();
  const { notification } = App.useApp();
  const [form] = Form.useForm<PlatformSettingsFormValues>();

  const { isAll, isPlatformSettingsEdit, isPlatformSettingsRead } =
    useAppSelector((state) => state.loggedAdmin.parsedPermissions);

  const canRead = isAll || isPlatformSettingsRead || isPlatformSettingsEdit;
  const canEdit = isAll || isPlatformSettingsEdit;

  const { data, isLoading } = useGetPlatformSettingsQuery(undefined, {
    skip: !canRead,
  });

  const [updateSettings, { isLoading: isSaving, error, isSuccess }] =
    useUpdatePlatformSettingsMutation();

  useEffect(() => {
    if (!canRead) {
      navigate(getRouteByName("NotFoundPage"));
    }
  }, [canRead, navigate]);

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        postExpireDays: data.postExpireDays,
        paidPostPricePerDay: data.paidPostPricePerDay,
        verifiedBadgePricePerMonth: data.verifiedBadgePricePerMonth,
        dailyQuotasEnabled: data.dailyQuotasEnabled,
        maxPostsPerDay: data.maxPostsPerDay,
        maxVideoPostsPerDay: data.maxVideoPostsPerDay,
        maxCommentsPerDay: data.maxCommentsPerDay,
        maxVideoCommentsPerDay: data.maxVideoCommentsPerDay,
        maxVideoDurationSeconds: data.maxVideoDurationSeconds,
        maxEventLiveDurationSeconds: data.maxEventLiveDurationSeconds,
        eventMaxRadiusKm: data.eventMaxRadiusKm,
      });
    }
  }, [data, form]);

  // Disable the four numeric inputs when the master toggle is off so
  // admins see at a glance that the values are inert. Watching the
  // form value (not the cached `data`) keeps the disabled state in
  // sync the moment the switch flips.
  const dailyQuotasEnabled = Form.useWatch("dailyQuotasEnabled", form);

  useEffect(() => {
    if (error) {
      notification.error({ message: getErrorMessage(error) });
    }
  }, [error, notification]);

  useEffect(() => {
    if (isSuccess) {
      notification.success({ message: "Setarile au fost salvate" });
    }
  }, [isSuccess, notification]);

  if (isLoading) {
    return <Skeleton active paragraph={{ rows: 6 }} />;
  }

  return (
    <>
      <Form<PlatformSettingsFormValues>
        form={form}
        layout="vertical"
        disabled={!canEdit}
        onFinish={(values) => {
          updateSettings(values);
        }}
      >
        <Flex
          justify="space-between"
          align="center"
          style={{ marginBottom: 16 }}
          gap={16}
          wrap="wrap"
        >
          <Typography.Title level={3} style={{ margin: 0 }}>
            Setari platforma
          </Typography.Title>
          <Button
            type="primary"
            htmlType="submit"
            loading={isSaving}
            disabled={!canEdit}
          >
            Salveaza
          </Button>
        </Flex>

        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card title="Postari promovate">
              <Form.Item
                name="paidPostPricePerDay"
                label="Pret pe zi (RON)"
                rules={[
                  { required: true, message: "Introdu un pret" },
                  {
                    type: "number",
                    min: 0,
                    message: "Pretul trebuie sa fie >= 0",
                  },
                ]}
                extra="Pret unic aplicat pentru fiecare zi selectata la crearea unei postari promovate."
              >
                <InputNumber
                  min={0}
                  step={1}
                  precision={2}
                  addonAfter="RON / zi"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} md={12}>
            <Card title="Postari gratuite">
              <Form.Item
                name="postExpireDays"
                label="Zile pana la expirare"
                rules={[
                  { required: true, message: "Introdu numarul de zile" },
                  {
                    type: "number",
                    min: 1,
                    message: "Minim 1 zi",
                  },
                ]}
                extra="Cate zile dupa publicare ramane activa o postare gratuita."
              >
                <InputNumber
                  min={1}
                  step={1}
                  precision={0}
                  addonAfter="zile"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} md={12}>
            <Card title="Bifa de verificare">
              <Form.Item
                name="verifiedBadgePricePerMonth"
                label="Pret pe luna (RON)"
                rules={[
                  { required: true, message: "Introdu un pret" },
                  {
                    type: "number",
                    min: 0,
                    message: "Pretul trebuie sa fie >= 0",
                  },
                ]}
                extra="Tarif lunar pentru abonamentul la bifa albastra de verificare."
              >
                <InputNumber
                  min={0}
                  step={1}
                  precision={2}
                  addonAfter="RON / luna"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} md={12}>
            <Card title="Videoclipuri">
              <Form.Item
                name="maxVideoDurationSeconds"
                label="Durata maxima per videoclip (secunde)"
                rules={[
                  { required: true, message: "Introdu o valoare" },
                  {
                    type: "number",
                    min: 5,
                    max: MAX_VIDEO_DURATION_CEILING_SECONDS,
                    message: `Valoarea trebuie sa fie intre 5 si ${MAX_VIDEO_DURATION_CEILING_SECONDS}`,
                  },
                ]}
                extra="Limita maxima aplicata oricarui videoclip incarcat — postare, comentariu sau repost cu citat. Modificarea se aplica imediat pe mobil prin invalidarea cache-ului."
              >
                <InputNumber
                  min={5}
                  max={MAX_VIDEO_DURATION_CEILING_SECONDS}
                  step={5}
                  precision={0}
                  addonAfter="secunde"
                  style={{ width: "100%" }}
                />
              </Form.Item>

              <Form.Item
                name="maxEventLiveDurationSeconds"
                label="Durata maxima per eveniment live (secunde)"
                rules={[
                  { required: true, message: "Introdu o valoare" },
                  {
                    type: "number",
                    min: MIN_EVENT_LIVE_DURATION_SECONDS,
                    max: MAX_EVENT_LIVE_DURATION_CEILING_SECONDS,
                    message: `Valoarea trebuie sa fie intre ${MIN_EVENT_LIVE_DURATION_SECONDS} si ${MAX_EVENT_LIVE_DURATION_CEILING_SECONDS}`,
                  },
                ]}
                extra="Limita maxima pentru o sesiune de inregistrare live in cadrul unui eveniment. Camera mobila opreste automat inregistrarea cand atinge pragul si initiaza upload-ul. Plafon de siguranta: 4 ore (14400 secunde)."
              >
                <InputNumber
                  min={MIN_EVENT_LIVE_DURATION_SECONDS}
                  max={MAX_EVENT_LIVE_DURATION_CEILING_SECONDS}
                  step={30}
                  precision={0}
                  addonAfter="secunde"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} md={12}>
            <Card title="Evenimente live">
              <Form.Item
                name="eventMaxRadiusKm"
                label="Raza maxima per eveniment (km)"
                rules={[
                  { required: true, message: "Introdu o valoare" },
                  {
                    type: "number",
                    min: EVENT_MIN_RADIUS_KM,
                    max: EVENT_MAX_RADIUS_CEILING_KM,
                    message: `Valoarea trebuie sa fie intre ${EVENT_MIN_RADIUS_KM} si ${EVENT_MAX_RADIUS_CEILING_KM}`,
                  },
                ]}
                extra="Raza maxima (in km) pe care un admin o poate seta pe un eveniment live. Controleaza plafonul slider-ului de la crearea / editarea evenimentului."
              >
                <InputNumber
                  min={EVENT_MIN_RADIUS_KM}
                  max={EVENT_MAX_RADIUS_CEILING_KM}
                  step={100}
                  precision={0}
                  addonAfter="km"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24}>
            <Card title="Limite zilnice">
              <Form.Item
                name="dailyQuotasEnabled"
                label="Activeaza limitele zilnice"
                valuePropName="checked"
                extra="Cand este oprit, niciun plafon nu se aplica si utilizatorii pot posta / comenta nelimitat. Conturile bot (is_bot=true) sunt mereu scutite."
              >
                <Switch />
              </Form.Item>

              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="maxPostsPerDay"
                    label="Postari maxim pe zi"
                    rules={[
                      { required: true, message: "Introdu o valoare" },
                      {
                        type: "number",
                        min: 1,
                        max: 1000,
                        message: "Valoarea trebuie sa fie intre 1 si 1000",
                      },
                    ]}
                    extra="Numar total de postari pe care un utilizator le poate publica intr-o zi."
                  >
                    <InputNumber
                      min={1}
                      max={1000}
                      step={1}
                      precision={0}
                      addonAfter="postari / zi"
                      disabled={!canEdit || !dailyQuotasEnabled}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="maxVideoPostsPerDay"
                    label="Postari cu video maxim pe zi"
                    rules={[
                      { required: true, message: "Introdu o valoare" },
                      {
                        type: "number",
                        min: 1,
                        max: 1000,
                        message: "Valoarea trebuie sa fie intre 1 si 1000",
                      },
                    ]}
                    extra="Sub-plafon: cate dintre postarile zilei pot avea video atasat."
                  >
                    <InputNumber
                      min={1}
                      max={1000}
                      step={1}
                      precision={0}
                      addonAfter="video / zi"
                      disabled={!canEdit || !dailyQuotasEnabled}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="maxCommentsPerDay"
                    label="Comentarii maxim pe zi"
                    rules={[
                      { required: true, message: "Introdu o valoare" },
                      {
                        type: "number",
                        min: 1,
                        max: 1000,
                        message: "Valoarea trebuie sa fie intre 1 si 1000",
                      },
                    ]}
                    extra="Numar total de comentarii pe care un utilizator le poate publica intr-o zi."
                  >
                    <InputNumber
                      min={1}
                      max={1000}
                      step={1}
                      precision={0}
                      addonAfter="comentarii / zi"
                      disabled={!canEdit || !dailyQuotasEnabled}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="maxVideoCommentsPerDay"
                    label="Comentarii cu video maxim pe zi"
                    rules={[
                      { required: true, message: "Introdu o valoare" },
                      {
                        type: "number",
                        min: 1,
                        max: 1000,
                        message: "Valoarea trebuie sa fie intre 1 si 1000",
                      },
                    ]}
                    extra="Sub-plafon: cate dintre comentariile zilei pot avea video atasat."
                  >
                    <InputNumber
                      min={1}
                      max={1000}
                      step={1}
                      precision={0}
                      addonAfter="video / zi"
                      disabled={!canEdit || !dailyQuotasEnabled}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Form>

      {/* Server-driven update gate — last card on the page because it
          is the only setting here that can lock users out of the app.
          Gated on canRead so it doesn't fire a doomed request in the
          frame before the permission redirect lands. */}
      {canRead && <AppUpdateSettingsCard canEdit={canEdit} />}
    </>
  );
};
